
import React, { useState, useEffect } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import { useParams } from "react-router-dom";


export default function AppointmentForm({ departmentId: propDepartmentId }) {
  const [additionalDetails, setAdditionalDetails] = useState("");


  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    reason: "",
    additionalDetails: "",
  });
  const [errors, setErrors] = useState({
    name: "",
    phone: "",
    email: "",
  });
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    let error = "";

    switch (name) {
      case "name":
        if (value && !/^[A-Za-z\s]+$/.test(value)) {
          error = "Name can only contain letters and spaces.";
        }
        break;

      case "phone":
        if (value && !/^\d+$/.test(value)) {
          error = "Phone number can contain digits only.";
        } else if (value.length > 15) {
          error = "Maximum 15 digits allowed.";
        }
        break;

      case "email":
        if (
          value &&
          !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)
        ) {
          error = "Please enter a valid email address.";
        }
        break;

      default:
        break;
    }

    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };
  const [caseFiles, setCaseFiles] = useState([]);
  const [xrayFiles, setXrayFiles] = useState([]);
  const [patientPhotos, setPatientPhotos] = useState([]);
  const [treatmentFormFiles, setTreatmentFormFiles] = useState([]);
  const [intraOralFiles, setIntraOralFiles] = useState({});
  const [extraOralFiles, setExtraOralFiles] = useState({});
  const [radiologyFiles, setRadiologyFiles] = useState({});
  const handleIntraOral = (key, file) => setIntraOralFiles(prev => ({ ...prev, [key]: file }));
  const handleExtraOral = (key, file) => setExtraOralFiles(prev => ({ ...prev, [key]: file }));
  const handleRadiology = (key, file) => setRadiologyFiles(prev => ({ ...prev, [key]: file }));
  const { departmentId: routeDepartmentId } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const initialDepartment = propDepartmentId || routeDepartmentId || "";
  const [selectedDepartment, setSelectedDepartment] = useState(initialDepartment);
  const isRecordsMode = selectedDepartment === "free-clear-aligner-case-evaluation";

  useEffect(() => {
    if (propDepartmentId || routeDepartmentId) {
      setSelectedDepartment(propDepartmentId || routeDepartmentId);
    }
  }, [propDepartmentId, routeDepartmentId]);
  // Helper: convert a File object to base64 string
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(",")[1];
        resolve({
          name: file.name,
          mimeType: file.type || "application/octet-stream",
          data: base64,
        });
      };
      reader.onerror = (err) => reject(err);
      reader.readAsDataURL(file);
    });
  };

  const SCRIPT_URL =
    "https://script.google.com/macros/s/AKfycbzUGA8MX-C5Y4M8iXBaKg8t2R-tJmrHw6-9mT5uNRi-POMNA5qKjZ2BpNOXQCWVQR4M/exec";

  // Single small JSON POST. Throws on network failure or backend error.
  const postToScript = async (payload) => {
    const response = await fetch(SCRIPT_URL, {
      method: "POST",
      headers: { "Content-Type": "text/plain;charset=utf-8" },
      body: JSON.stringify(payload),
    });

    const text = await response.text();
    let result;
    try {
      result = JSON.parse(text);
    } catch (parseErr) {
      throw new Error("Unexpected response from server: " + text);
    }

    if (!result.success) {
      throw new Error(result.error || "Unknown server error");
    }
    return result;
  };

  // Uploads each file in `files` as its own small request, tagged with submissionId + category.
  const uploadFiles = async (files, formType, submissionId, category) => {
    for (const file of files) {
      const encoded = await fileToBase64(file);
      await postToScript({ formType, submissionId, category, file: encoded });
    }
  };

  // Submit handler for the standard Appointment form (name/phone/email/case type etc.)
  const handleAppointmentSubmit = async (e) => {
    e.preventDefault();

    if (!selectedDepartment) {
      alert("Please select a Case Type");
      return;
    }

    if (!caseFiles || caseFiles.length === 0) {
      alert("Please upload at least one Case file");
      return;
    }
    if (!xrayFiles || xrayFiles.length === 0) {
      alert("Please upload at least one OPG/X-RAY file");
      return;
    }
    if (!patientPhotos || patientPhotos.length === 0) {
      alert("Please upload at least one Patient Photo");
      return;
    }
    if (!treatmentFormFiles || treatmentFormFiles.length === 0) {
      alert("Please upload Treatment Form filled by the doctor");
      return;
    }

    setIsSubmitting(true);
    try {
      const { submissionId } = await postToScript({
        formType: "appointment_start",
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        reason: formData.reason,
        additionalDetails: formData.additionalDetails,
        caseType: selectedDepartment,
      });

      await uploadFiles(caseFiles, "appointment_file", submissionId, "caseFiles");
      await uploadFiles(xrayFiles, "appointment_file", submissionId, "xrayFiles");
      await uploadFiles(patientPhotos, "appointment_file", submissionId, "patientPhotos");
      await uploadFiles(treatmentFormFiles, "appointment_file", submissionId, "treatmentForm");

      await postToScript({ formType: "appointment_finish", submissionId });

      alert("Form submitted successfully");
      setFormData({ name: "", phone: "", email: "", reason: "", additionalDetails: "" });
      setSelectedDepartment("");
      setCaseFiles([]);
      setXrayFiles([]);
      setPatientPhotos([]);
      setTreatmentFormFiles([]);
    } catch (error) {
      console.error("Submission error:", error);
      alert("Submission failed: " + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Submit handler for the Records Required form (radiology + intra/extra oral photos)
  const handleRecordsSubmit = async (e) => {
    e.preventDefault();

    const radiologyList = Object.values(radiologyFiles).filter(Boolean);
    const intraOralList = Object.values(intraOralFiles).filter(Boolean);
    const extraOralList = Object.values(extraOralFiles).filter(Boolean);

    if (radiologyList.length === 0) {
      alert("Please upload at least one Radiology/X-RAY file");
      return;
    }
    if (intraOralList.length === 0 || extraOralList.length === 0) {
      alert("Please upload the Intra-oral and Extra-oral photos");
      return;
    }

    setIsSubmitting(true);
    try {
      const { submissionId } = await postToScript({
        formType: "records_start",
        additionalDetails,
      });

      await uploadFiles(radiologyList, "records_file", submissionId, "radiologyFiles");
      await uploadFiles(intraOralList, "records_file", submissionId, "intraOralPhotos");
      await uploadFiles(extraOralList, "records_file", submissionId, "extraOralPhotos");

      await postToScript({ formType: "records_finish", submissionId });

      alert("Records submitted successfully");
      setAdditionalDetails("");
      setRadiologyFiles({});
      setIntraOralFiles({});
      setExtraOralFiles({});
      setSelectedDepartment("");
    } catch (error) {
      console.error("Submission error:", error);
      alert("Submission failed: " + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <form action="#" className="row" onSubmit={handleAppointmentSubmit} data-aos="fade-up"
        data-aos-duration="1200"
        data-aos-offset="200">
        {!isRecordsMode && (
          <div className="row">
            <div className="col-lg-4">
              <label className="cs_input_label cs_heading_color">Name <span className="text-danger">*</span></label>
              <input name="name" type="text" className="cs_form_field" placeholder="David John" required value={formData.name} onChange={handleChange} />
              {errors.name && (
                <small className="text-danger">{errors.name}</small>
              )}
            </div>
            <div className="col-lg-4">
              <label className="cs_input_label cs_heading_color">Phone Number <span className="text-danger">*</span></label>
              <input
                type="text"
                className="cs_form_field"
                required
                name="phone"
                placeholder="(123) 456 - 789"
                value={formData.phone}
                onChange={handleChange}
              />
              {errors.phone && (
                <small className="text-danger">{errors.phone}</small>
              )}
            </div>
            <div className="col-lg-4">
              <label className="cs_input_label cs_heading_color">
                Reason of Appointment
              </label>
              <input
                type="text"
                className="cs_form_field"
                placeholder="Message"
                name="reason"
                value={formData.reason}
                onChange={handleChange}
              />
              <div className="cs_height_42 cs_height_xl_25" />
            </div>
            <div className="col-lg-4">
              <label className="cs_input_label cs_heading_color">Email</label>
              <input type="text" className="cs_form_field" placeholder="test@gmail.com" name="email" value={formData.email}
                onChange={handleChange} />
              {errors.email && (
                <small className="text-danger">{errors.email}</small>
              )}
            </div>
            <div className="col-lg-4">
              <label className="cs_input_label cs_heading_color">
                Upload Cases <span className="text-danger">*</span>
              </label>

              <label className="upload-box">
                <input
                  type="file"
                  multiple
                  className="d-none"
                  accept="
                  .stl,
                  .obj,
                  .ply,
                  .3ds,
                  .dxf,
                  .zpr,
                  .dicom,
                  .dcm

                "
                  name="cases"
                  onChange={(e) => setCaseFiles([...e.target.files])}
                />

                <div className="upload-content">
                  <i className="fa-solid fa-cloud-arrow-up"></i>
                  <h6>Upload Files</h6>
                  <span>STL, OBJ, PLY, 3DS, DXF, ZPR, DICOM, DCM</span>
                </div>
              </label>

              {caseFiles.length > 0 && (
                <div className="mt-2">
                  {caseFiles.map((file, index) => (
                    <div key={index} className="small mb-1">
                      📄 {file.name}
                    </div>
                  ))}
                </div>
              )}

              <div className="cs_height_42 cs_height_xl_25" />
            </div>

            <div className="col-lg-4">
              <label className="cs_input_label cs_heading_color">
                Upload OPG/ X-RAY <span className="text-danger">*</span>
              </label>

              <label className="upload-box">
                <input
                  type="file"
                  multiple
                  className="d-none"
                  accept="
                 .jpg,
                 .png,
                 .jpeg
                "
                  name="xray"
                  onChange={(e) => setXrayFiles([...e.target.files])}
                />

                <div className="upload-content">
                  <i className="fa-solid fa-cloud-arrow-up"></i>
                  <h6>Upload Files</h6>
                  <span>jpg, png, jpeg</span>
                </div>
              </label>

              {xrayFiles.length > 0 && (
                <div className="mt-2">
                  {xrayFiles.map((file, index) => (
                    <div key={index} className="small mb-1">
                      📄 {file.name}
                    </div>
                  ))}
                </div>
              )}

              <div className="cs_height_42 cs_height_xl_25" />
            </div>

            <div className="col-lg-4">
              <label className="cs_input_label cs_heading_color">
                Upload Patient Photos <span className="text-danger">*</span>
              </label>

              <label className="upload-box">
                <input
                  type="file"
                  multiple
                  className="d-none"
                  accept="
                  .jpg,
                  .jpeg,
                  .png,
                "
                  name="photos"
                  onChange={(e) => setPatientPhotos([...e.target.files])}
                />

                <div className="upload-content">
                  <i className="fa-solid fa-cloud-arrow-up"></i>
                  <h6>Upload Files</h6>
                  <span>JPG, JPEG, PNG</span>
                </div>
              </label>

              {patientPhotos.length > 0 && (
                <div className="mt-2">
                  {patientPhotos.map((file, index) => (
                    <div key={index} className="small mb-1">
                      📄 {file.name}
                    </div>
                  ))}
                </div>
              )}

              <div className="cs_height_42 cs_height_xl_25" />
            </div>

            <div className="col-lg-4">
              <label className="cs_input_label cs_heading_color">
                Upload Treatment Form <span className="text-danger">*</span>
              </label>

              <label className="upload-box">
                <input
                  type="file"
                  multiple
                  className="d-none"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  name="treatmentForm"
                  onChange={(e) => setTreatmentFormFiles([...e.target.files])}
                />

                <div className="upload-content">
                  <i className="fa-solid fa-cloud-arrow-up"></i>
                  <h6>Upload Files</h6>
                  <span>PDF, DOC, DOCX, JPG, PNG</span>
                </div>
              </label>

              {treatmentFormFiles.length > 0 && (
                <div className="mt-2">
                  {treatmentFormFiles.map((file, index) => (
                    <div key={index} className="small mb-1">
                      📄 {file.name}
                    </div>
                  ))}
                </div>
              )}

              <div className="cs_height_42 cs_height_xl_25" />
            </div>
          <div className="col-lg-4">
            <label className="cs_input_label cs_heading_color">
              Additional Details
            </label>
            <input
              type="text"
              name="additionalDetails"
              className="cs_form_field"
              placeholder="Message"
              value={formData.additionalDetails}
              onChange={handleChange}
            />
            <div className="cs_height_42 cs_height_xl_25" />
          </div>
           <div className="col-lg-12">
        <label className="cs_input_label cs_heading_color">Case Type <span className="text-danger">*</span></label>

        <div className="cs_radio_group department_group">

          <div className="cs_radio_wrap">
            <input
              className="cs_radio_input"
              type="radio"
              name="department"
              id="freeClearAligner"
              value="freeClearAligner"
             checked={selectedDepartment === "free-clear-aligner-case-evaluation"}
              onChange={() =>
                setSelectedDepartment("free-clear-aligner-case-evaluation")
              }
            />
            <label className="cs_radio_label" htmlFor="freeClearAligner">
              Free Clear Aligner Case Evaluation
            </label>
          </div>

          <div className="cs_radio_wrap">
            <input
              className="cs_radio_input"
              type="radio"
              name="department"
              id="clearAligner"
              value="clearAligner"
             checked={selectedDepartment === "clear-aligner-treatment-planning"}
              onChange={() =>
                setSelectedDepartment("clear-aligner-treatment-planning")
              }
            />
            <label className="cs_radio_label" htmlFor="clearAligner">
              Clear Aligner Treatment Planning
            </label>
          </div>

          <div className="cs_radio_wrap">
            <input
              className="cs_radio_input"
              type="radio"
              name="department"
              id="orthoCadDesign"
              value="orthoCadDesign"
              checked={selectedDepartment === "orthodontic-cad-design-services"}
              onChange={() =>
                setSelectedDepartment("orthodontic-cad-design-services")
              }
            />
            <label className="cs_radio_label" htmlFor="orthoCadDesign">
              Orthodontic CAD Design Services
            </label>
          </div>

          <div className="cs_radio_wrap">
  <input
    className="cs_radio_input"
    type="radio"
    name="department"
    id="retainerDesign"
    value="retainerDesign"
    checked={selectedDepartment === "fixed-and-removable-retainer-design"}
    onChange={() =>
      setSelectedDepartment("fixed-and-removable-retainer-design")
    }
  />
  <label className="cs_radio_label" htmlFor="retainerDesign">
    Fixed and Removable Retainer Design
  </label>
            </div>

            <div className="cs_radio_wrap">
              <input
                className="cs_radio_input"
                type="radio"
                name="department"
                id="sleepApnea"
                value="sleepApnea"
                checked={selectedDepartment === "sleep-apnea-mad-device-design"}
                onChange={() =>
                  setSelectedDepartment("sleep-apnea-mad-device-design")
                }
              />
              <label className="cs_radio_label" htmlFor="sleepApnea">
                Sleep Apnea &amp; MAD Device Design
              </label>
            </div>

            <div className="cs_radio_wrap">
              <input
                className="cs_radio_input"
                type="radio"
                name="department"
                id="surgicalGuide"
                value="surgicalGuide"
                checked={selectedDepartment === "surgical-guide-design-for-implantology"}
                onChange={() =>
                  setSelectedDepartment("surgical-guide-design-for-implantology")
                }
              />
              <label className="cs_radio_label" htmlFor="surgicalGuide">
                Surgical Guide Design for Implantology
              </label>
            </div>

            <div className="cs_radio_wrap">
              <input
                className="cs_radio_input"
                type="radio"
                name="department"
                id="digitalModel"
                value="digitalModel"
                checked={selectedDepartment === "digital-model-analysis-and-setup"}
                onChange={() =>
                  setSelectedDepartment("digital-model-analysis-and-setup")
                }
              />
              <label className="cs_radio_label" htmlFor="digitalModel">
                Digital Model Analysis and Setup
              </label>
            </div>

            {/* <div className="cs_radio_wrap">
              <input
                className="cs_radio_input"
                type="radio"
                name="department"
                id="treatmentSimulation"
                value="treatmentSimulation"
                checked={selectedDepartment === "3d-orthodontic-treatment-simulation"}
                onChange={() =>
                  setSelectedDepartment("3d-orthodontic-treatment-simulation")
                }
              />
              <label className="cs_radio_label" htmlFor="treatmentSimulation">
                3D Orthodontic Treatment Simulation
              </label>
            </div> */}

            <div className="cs_radio_wrap">
              <input
                className="cs_radio_input"
                type="radio"
                name="department"
                id="cadCamConsultation"
                value="cadCamConsultation"
                checked={selectedDepartment === "cad-cam-workflow-consultation"}
                onChange={() =>
                  setSelectedDepartment("cad-cam-workflow-consultation")
                }
              />
              <label className="cs_radio_label" htmlFor="cadCamConsultation">
                CAD/CAM Workflow Consultation
              </label>
            </div>

        </div>

        <div className="cs_height_42 cs_height_xl_25" />
      </div>
            <div className="col-lg-12">
              <button className="cs_btn cs_style_1" type="submit" disabled={isSubmitting}>
                <span>{isSubmitting ? "Submitting..." : "Submit"}</span>
                <i>
                  {isSubmitting ? (
                    <span className="spinner" />
                  ) : (
                    <>
                      <img src="/images/icons/arrow_white.svg" alt="Icon" />
                      <img src="/images/icons/arrow_white.svg" alt="Icon" />
                    </>
                  )}
                </i>
              </button>
            </div>
          </div>
        )}
      </form>
      {isRecordsMode && (
        <form action="#" className="row" onSubmit={handleRecordsSubmit} data-aos="fade-up" data-aos-duration="1200" data-aos-offset="200">
          <div className="col-lg-12">
            <h4 className="cs_fs_24 cs_semibold mb-4 mt-4 cs_heading_color text-center">Records Required</h4>
            <h5 className="cs_fs_24 cs_semibold mb-4 mt-4 cs_heading_color text-center">Treatment form to be filled by doctor</h5>
            <div className="mb-4">
              <h5 className="cs_fs_20 cs_semibold mb-3">Upload OPG/ X-RAY <span className="text-danger">*</span></h5>
              <div className="row">
                <div className="col-md-3 mb-3">
                  <label className="upload-box w-100">
                    <input type="file" className="d-none" accept=".stl" onChange={(e) => handleRadiology('lateralCeph', e.target.files[0])} />
                    <div className="upload-content text-center">
                      <i className="fa-solid fa-cloud-arrow-up"></i>
                      <h6>Lateral cephalogram</h6>
                      {radiologyFiles.lateralCeph && <span className="text-success small d-block">{radiologyFiles.lateralCeph.name}</span>}
                    </div>
                  </label>
                </div>
                <div className="col-md-3 mb-3">
                  <label className="upload-box w-100">
                    <input type="file" className="d-none" accept=".stl" onChange={(e) => handleRadiology('opg', e.target.files[0])} />
                    <div className="upload-content text-center">
                      <i className="fa-solid fa-cloud-arrow-up"></i>
                      <h6>OPG</h6>
                      {radiologyFiles.opg && <span className="text-success small d-block">{radiologyFiles.opg.name}</span>}
                    </div>
                  </label>
                </div>
                <div className="col-md-3 mb-3">
                  <label className="upload-box w-100">
                    <input type="file" className="d-none" accept=".stl" onChange={(e) => handleRadiology('cbct', e.target.files[0])} />
                    <div className="upload-content text-center">
                      <i className="fa-solid fa-cloud-arrow-up"></i>
                      <h6>CBCT (if required)</h6>
                      {radiologyFiles.cbct && <span className="text-success small d-block">{radiologyFiles.cbct.name}</span>}
                    </div>
                  </label>
                </div>
              </div>
            </div>

            <div className="mb-4">
              <h5 className="cs_fs_20 cs_semibold mb-3">Photographs: Intra oral 5 views <span className="text-danger">*</span></h5>
              <div className="row">
                {['frontal', 'rightLateral', 'leftLateral', 'upperOcclusal', 'lowerOcclusal'].map((view) => (
                  <div className="col-md-2 mb-3" key={view}>
                    <label className="upload-box w-100" style={{ padding: '20px 10px' }}>
                      <input type="file" className="d-none" accept=".jpg,.jpeg,.png" onChange={(e) => handleIntraOral(view, e.target.files[0])} />
                      <div className="upload-content text-center">
                        <i className="fa-solid fa-cloud-arrow-up mb-2"></i>
                        <h6 className="cs_fs_14 m-0">{view.replace(/([A-Z])/g, ' $1').trim()}</h6>
                        {intraOralFiles[view] && (
                          <span className="text-success small text-truncate d-block" style={{ maxWidth: '100px' }}>
                            {intraOralFiles[view].name}
                          </span>
                        )}
                      </div>
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-4">
              <h5 className="cs_fs_20 cs_semibold mb-3">Photographs: Extra oral 6 views <span className="text-danger">*</span></h5>
              <div className="row">
                {['frontal', 'frontalSmile', 'oblique', 'obliqueSmile', 'lateral', 'lateralSmile'].map((view) => (
                  <div className="col-md-2 mb-3" key={view}>
                    <label className="upload-box w-100" style={{ padding: '20px 10px' }}>
                      <input type="file" className="d-none" accept=".jpg,.jpeg,.png" onChange={(e) => handleExtraOral(view, e.target.files[0])} />
                      <div className="upload-content text-center">
                        <i className="fa-solid fa-cloud-arrow-up mb-2"></i>
                        <h6 className="cs_fs_14 m-0">{view.replace(/([A-Z])/g, ' $1').trim()}</h6>
                        {extraOralFiles[view] && (
                          <span className="text-success small text-truncate d-block" style={{ maxWidth: '100px' }}>
                            {extraOralFiles[view].name}
                          </span>
                        )}
                      </div>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="col-lg-4">
            <label className="cs_input_label cs_heading_color">
              Additional Details
            </label>
            <input
              type="text"
              name="additionalDetails"
              className="cs_form_field"
              placeholder="Message"
              value={additionalDetails}
              onChange={(e) => setAdditionalDetails(e.target.value)}
            />
            <div className="cs_height_42 cs_height_xl_25" />
          </div>

          <div className="col-lg-12">
            <button className="cs_btn cs_style_1" type="submit" disabled={isSubmitting}>
              <span>{isSubmitting ? "Submitting..." : "Submit"}</span>
              <i>
                {isSubmitting ? (
                  <span className="spinner" />
                ) : (
                  <>
                    <img src="/images/icons/arrow_white.svg" alt="Icon" />
                    <img src="/images/icons/arrow_white.svg" alt="Icon" />
                  </>
                )}
              </i>
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
